import { Popover } from '@base-ui/react/popover';
import { ArrowUpRight } from 'lucide-react';

const chipClassName =
  'inline-flex h-6 max-w-full min-w-0 shrink-0 cursor-pointer items-center gap-1 rounded-full border-0 bg-chip px-2 text-xs font-normal text-ink-muted no-underline transition-[background-color,color,transform] duration-150 ease-out hover:bg-line hover:text-ink active:scale-98 data-[popup-open]:bg-line data-[popup-open]:text-ink motion-reduce:transition-none';

type DirectorySourceLinksProps = {
  label: string;
  urls: readonly string[] | null | undefined;
};

const DirectorySourceLinks = ({ label, urls }: DirectorySourceLinksProps) => {
  const [firstUrl, ...otherUrls] = urls ?? [];

  if (!firstUrl) {
    return null;
  }

  const firstHost = formatSourceHost(firstUrl);

  if (otherUrls.length === 0) {
    return (
      <a
        aria-label={`Source for ${label}: ${firstHost} (opens in a new tab)`}
        className={chipClassName}
        href={firstUrl}
        rel="nofollow noopener"
        target="_blank"
      >
        <span className="truncate">{firstHost}</span>
        <ArrowUpRight aria-hidden="true" className="size-3 shrink-0" />
      </a>
    );
  }

  const allUrls = [firstUrl, ...otherUrls];

  return (
    <Popover.Root>
      <Popover.Trigger
        aria-label={`${allUrls.length} sources for ${label}`}
        className={chipClassName}
      >
        <span className="truncate">{firstHost}</span>
        <span className="shrink-0 tabular-nums">+{otherUrls.length}</span>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Positioner align="start" className="z-50" sideOffset={6}>
          <Popover.Popup className="surface-card w-72 max-w-[calc(100vw-32px)] origin-[var(--transform-origin)] p-2 transition-[transform,opacity] duration-150 ease-out data-[ending-style]:scale-95 data-[ending-style]:opacity-0 data-[starting-style]:scale-95 data-[starting-style]:opacity-0 motion-reduce:transition-none">
            <Popover.Title className="m-0 px-3 pt-2 pb-1 text-xs font-medium text-ink-muted">
              Sources for {label}
            </Popover.Title>
            <ul className="m-0 flex list-none flex-col p-0">
              {allUrls.map((url) => (
                <li key={url}>
                  <a
                    className="flex items-center gap-3 rounded-xl px-3 py-2 text-ink no-underline transition-[background-color] duration-150 ease-out hover:bg-canvas motion-reduce:transition-none"
                    href={url}
                    rel="nofollow noopener"
                    target="_blank"
                  >
                    <span className="flex min-w-0 flex-1 flex-col">
                      <span className="truncate text-sm font-medium">
                        {formatSourceHost(url)}
                      </span>
                      <span className="truncate text-xs text-ink-muted">
                        {formatSourcePath(url)}
                      </span>
                    </span>
                    <ArrowUpRight
                      aria-hidden="true"
                      className="size-4 shrink-0 text-ink-muted"
                    />
                  </a>
                </li>
              ))}
            </ul>
          </Popover.Popup>
        </Popover.Positioner>
      </Popover.Portal>
    </Popover.Root>
  );
};

const parseSourceUrl = (url: string) => {
  try {
    return new URL(url);
  } catch {
    return null;
  }
};

const formatSourceHost = (url: string) =>
  parseSourceUrl(url)?.hostname.replace(/^www\./, '') ?? url;

const formatSourcePath = (url: string) => {
  const parsedUrl = parseSourceUrl(url);

  if (!parsedUrl) {
    return url;
  }

  const path = `${parsedUrl.pathname}${parsedUrl.search}`;

  return path === '/' ? parsedUrl.hostname : path;
};

export default DirectorySourceLinks;
